import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Hero() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const section = sectionRef.current;
          if (section) {
            const rect = section.getBoundingClientRect();
            const progress = Math.max(0, -rect.top);
            setScrollY(progress);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const p = (rate: number) => `translateY(${scrollY * rate}px)`;

  return (
    <section
      ref={sectionRef}
      className="relative flex items-center justify-center pt-20 pb-10 overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #0c0814 0%, #130920 50%, #0a0618 100%)' }}
    >
      {/* Grid background – slow parallax */}
      <div
        className="absolute inset-0 grid-bg pointer-events-none"
        style={{ transform: p(0.15), willChange: 'transform' }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[
          { left: '12%', top: '18%', size: 3, delay: '0s', dur: '6s' },
          { left: '78%', top: '25%', size: 2, delay: '1.2s', dur: '8s' },
          { left: '55%', top: '65%', size: 4, delay: '0.6s', dur: '7s' },
          { left: '30%', top: '78%', size: 2, delay: '2s', dur: '5s' },
          { left: '88%', top: '55%', size: 3, delay: '0.3s', dur: '9s' },
          { left: '6%', top: '55%', size: 2, delay: '1.8s', dur: '6.5s' },
          { left: '65%', top: '12%', size: 2, delay: '0.9s', dur: '7.5s' },
          { left: '42%', top: '42%', size: 3, delay: '1.5s', dur: '8.5s' },
        ].map((dot, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-purple-400/30"
            style={{
              left: dot.left,
              top: dot.top,
              width: dot.size,
              height: dot.size,
              transform: p(0.08 + i * 0.02),
              animation: `floatDot ${dot.dur} ${dot.delay} ease-in-out infinite alternate`,
              willChange: 'transform',
            }}
          />
        ))}
      </div>

      {/* Radial glow spots – each layer moves at different parallax rate */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Main center glow – medium parallax */}
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[450px] rounded-full opacity-25"
          style={{
            background: 'radial-gradient(ellipse, rgba(168,85,247,0.35) 0%, transparent 70%)',
            transform: `translateX(-50%) ${p(0.22)}`,
            willChange: 'transform',
          }}
        />
        {/* Bottom-left glow – faster */}
        <div
          className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full opacity-12"
          style={{
            background: 'radial-gradient(circle, rgba(124,58,237,0.4) 0%, transparent 70%)',
            transform: p(0.35),
            willChange: 'transform',
          }}
        />
        {/* Top-right glow – slow */}
        <div
          className="absolute top-1/4 right-1/5 w-72 h-72 rounded-full opacity-10"
          style={{
            background: 'radial-gradient(circle, rgba(232,121,249,0.3) 0%, transparent 70%)',
            transform: p(0.1),
            willChange: 'transform',
          }}
        />
        {/* Small accent glow */}
        <div
          className="absolute bottom-1/3 right-1/3 w-40 h-40 rounded-full opacity-8"
          style={{
            background: 'radial-gradient(circle, rgba(250,204,21,0.15) 0%, transparent 70%)',
            transform: p(0.28),
            willChange: 'transform',
          }}
        />
        {/* Extra deep glow for depth */}
        <div
          className="absolute top-0 right-1/4 w-[500px] h-[300px] rounded-full opacity-8"
          style={{
            background: 'radial-gradient(ellipse, rgba(147,51,234,0.2) 0%, transparent 70%)',
            transform: p(0.05),
            willChange: 'transform',
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        {/* Left Content – slight upward parallax */}
        <div style={{ transform: p(0.18), willChange: 'transform' }}>
          {/* Badge */}
          <div className="badge-purple mb-4">
            <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse"></div>
            {t('hero.badge')}
          </div>

          <h1
            className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-4 tracking-tight font-display"
            style={{ lineHeight: '1.02' }}
          >
            {t('hero.title1')}
            <br />
            <span className="text-gradient-aurora">{t('hero.title2')}</span>
          </h1>

          <p className="text-sm md:text-lg text-white/65 leading-relaxed mb-6 max-w-lg">
            {t('hero.desc')}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-7">
            <a
              href="https://www.polywin.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-3 rounded-full font-bold transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer text-sm text-white"
              style={{
                background: 'linear-gradient(135deg,#9333ea,#7c3aed)',
                boxShadow: '0 4px 24px rgba(147,51,234,0.45)',
              }}
            >
              {t('hero.ctaPrimary')}
              <i className="ri-arrow-right-line text-base"></i>
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 md:gap-4 pt-5 border-t border-purple-500/15">
            {[
              { value: '247+', labelKey: 'hero.statsEvents' },
              { value: '99.7%', labelKey: 'hero.statsSuccessRate' },
              { value: '<200ms', labelKey: 'hero.statsExecTime' },
            ].map(s => (
              <div key={s.labelKey}>
                <div className="text-lg md:text-2xl font-bold text-purple-400 mb-1">{s.value}</div>
                <div className="text-xs md:text-sm text-white/60 font-normal">{t(s.labelKey)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right – Probability Card – faster parallax for depth contrast */}
        <div
          className="relative mt-2 md:mt-0"
          style={{ transform: p(0.08), willChange: 'transform' }}
        >
          <div
            className="premium-card premium-card-hover rounded-2xl p-4 md:p-7 cursor-default"
            style={{ boxShadow: '0 0 60px rgba(168,85,247,0.12)' }}
          >
            <div className="flex items-center justify-between mb-4 md:mb-5">
              <h3 className="text-base md:text-lg font-bold text-white font-heading">
                {t('hero.cardTitle')}
              </h3>
              <span className="px-2 md:px-3 py-1 bg-purple-400/10 border border-purple-400/25 text-purple-300 rounded-full text-xs font-bold whitespace-nowrap">
                {t('hero.cardBadge')}
              </span>
            </div>

            {/* Chart */}
            <div className="relative h-32 md:h-36 mb-4 md:mb-5 bg-white/[0.03] border border-purple-500/10 rounded-xl p-3 md:p-4 flex items-end justify-around gap-4">
              <div className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full bg-white/8 rounded-md overflow-hidden" style={{ height: '80px' }}>
                  <div className="w-full bg-white/20 rounded-md" style={{ height: '60%', marginTop: '40%' }}></div>
                </div>
                <div className="text-center">
                  <div className="text-lg md:text-xl font-black text-white/70">60%</div>
                  <div className="text-xs text-white/50">{t('hero.cardMarketProb')}</div>
                </div>
              </div>
              <div className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full bg-purple-400/15 rounded-md overflow-hidden" style={{ height: '80px' }}>
                  <div
                    className="w-full rounded-md"
                    style={{
                      height: '72%',
                      marginTop: '28%',
                      background: 'linear-gradient(180deg, #c084fc, #9333ea)',
                      boxShadow: '0 0 12px rgba(168,85,247,0.4)',
                    }}
                  ></div>
                </div>
                <div className="text-center">
                  <div className="text-lg md:text-xl font-black text-purple-400">72%</div>
                  <div className="text-xs text-white/50">{t('hero.cardAiProb')}</div>
                </div>
              </div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div
                  className="text-white px-2 md:px-3 py-1 md:py-1.5 rounded-full text-sm font-black whitespace-nowrap"
                  style={{
                    background: 'linear-gradient(135deg,#9333ea,#7c3aed)',
                    boxShadow: '0 4px 16px rgba(147,51,234,0.5)',
                  }}
                >
                  +12%
                </div>
              </div>
            </div>

            {/* Data Grid */}
            <div className="grid grid-cols-2 gap-2 md:gap-3">
              <div className="bg-white/[0.04] border border-purple-500/12 rounded-xl p-2.5 md:p-4">
                <div className="text-xs text-white/55 font-semibold mb-1">{t('hero.cardMarketImplied')}</div>
                <div className="text-xl md:text-3xl font-black text-white">60%</div>
              </div>
              <div className="bg-purple-400/[0.07] border border-purple-400/20 rounded-xl p-2.5 md:p-4">
                <div className="text-xs text-purple-300 font-semibold mb-1">{t('hero.cardAiTrue')}</div>
                <div className="text-xl md:text-3xl font-black text-purple-400">72%</div>
              </div>
              <div
                className="col-span-2 rounded-xl p-2.5 md:p-4"
                style={{
                  background: 'linear-gradient(135deg,#9333ea,#7c3aed)',
                  boxShadow: '0 4px 20px rgba(147,51,234,0.35)',
                }}
              >
                <div className="text-xs font-semibold mb-0.5 text-white/75">{t('hero.cardProbDiff')}</div>
                <div className="text-2xl md:text-4xl font-black text-white">12%</div>
              </div>
            </div>
          </div>

          {/* Glow blobs – independent parallax */}
          <div
            className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-3xl opacity-15 pointer-events-none"
            style={{
              background: 'radial-gradient(circle, #9333ea, transparent)',
              transform: p(0.3),
              willChange: 'transform',
            }}
          ></div>
          <div
            className="absolute -bottom-8 -left-8 w-36 h-36 rounded-full blur-3xl opacity-12 pointer-events-none"
            style={{
              background: 'radial-gradient(circle, #7c3aed, transparent)',
              transform: p(0.42),
              willChange: 'transform',
            }}
          ></div>
        </div>
      </div>

      <style>{`
        @keyframes floatDot {
          from { transform: translateY(0px) scale(1); opacity: 0.2; }
          to   { transform: translateY(-18px) scale(1.4); opacity: 0.6; }
        }
      `}</style>
    </section>
  );
}
